import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api.service';
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  InputNumber,
  Space,
  Typography,
  Row,
  Col,
  Table,
  Divider,
  message,
  Spin,
  Switch,
  Popover,
  Alert,
  Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuotation, useCreateQuotation, useUpdateQuotation } from '../../hooks/useQuotation';
import { canEditQuotation } from '../../utils/documentDeletePolicy';
import { useCustomers } from '../../hooks/useCustomers';
import { useOpportunities, useOpportunity } from '../../hooks/useOpportunities';
import orderReviewChecklistService from '../../services/orderReviewChecklist.service';
import customerService from '../../services/customer.service';
import { QuotationRequest, QuotationItemRequest } from '../../services/quotation.service';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCurrency } from '@/contexts/CurrencyContext';
import { PaymentTermSelect } from '@/components/finance/PaymentTermSelect';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useApplyUserDefaultDepartment } from '@/utils/userDefaultDepartment';
import {
  buildRentalSpecificationsSummary,
  calculateQuotationLineTotals,
  deriveRentalDuration,
  formatRentalQuantity,
  hasAnyRentalQuotationLine,
  isRentalQuotationItem,
  RENTAL_PERIOD_OPTIONS,
  showRentalFieldEditors,
} from '@/utils/quotationRentalDisplay';
import { ProjectSelector } from '@/components/project/ProjectSelector';
import { FormattedTextArea } from '@/components/form/FormattedTextArea';
import { useQuotationDefaults } from '@/hooks/useQuotationDefaults';

const { Title, Text } = Typography;

function formatCustomerBillingAddress(customer: Record<string, unknown>): string {
  const parts = [
    customer.billing_address_line1,
    customer.billing_address_line2,
    customer.billing_city,
    customer.billing_state,
    customer.billing_postal_code,
    customer.billing_country,
  ]
    .map((part) => String(part ?? '').trim())
    .filter(Boolean);
  return parts.join(', ');
}

interface QuotationItemFormData extends QuotationItemRequest {
  key: string;
  line_total?: number;
  net_amount?: number;
}

const QuotationFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const opportunityIdFromUrl = searchParams.get('opportunity_id');
  const [form] = Form.useForm();
  const { baseCurrency } = useCurrency();
  
  const isEditMode = !!id;
  useApplyUserDefaultDepartment(form, !isEditMode);
  
  const { data: quotationData, isLoading: quotationLoading } = useQuotation(id!);
  const { data: customersData } = useCustomers();
  const { data: opportunities = [] } = useOpportunities({ status: 'Open' });
  const createMutation = useCreateQuotation();
  const updateMutation = useUpdateQuotation();
  const { data: quotationDefaults } = useQuotationDefaults();

  const applyCompanyDefaults = () => {
    if (!quotationDefaults) return;
    const isEmpty = (v: unknown) => !String(v ?? '').replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ').trim();
    const fields = ['payment_terms', 'delivery_terms', 'notes', 'terms_and_conditions', 'special_instructions'] as const;
    const filled = fields.filter((f) => isEmpty(form.getFieldValue(f)) && !isEmpty(quotationDefaults[f]));
    if (!filled.length) {
      message.info('Nothing to fill — these fields already have text or no company default is set');
      return;
    }
    form.setFieldsValue(Object.fromEntries(filled.map((f) => [f, quotationDefaults[f]])));
    message.success(`Filled ${filled.length} empty field(s) from the company quotation defaults. Save to keep them.`);
  };

  const { data: revenueServiceTypes = [] } = useQuery({
    queryKey: ['sales-revenue-service-types', 'active'],
    queryFn: async () => {
      const res = await api.get('/sales/revenue-service-types?only_active=true');
      const body = (res as any)?.data ?? res;
      const list = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
      return list as { id: string; service_code: string; service_name: string }[];
    },
  });
  const rstOptions = useMemo(
    () => revenueServiceTypes.map((r) => ({ value: r.id, label: `${r.service_code} — ${r.service_name}` })),
    [revenueServiceTypes]
  );

  const watchedOppId = Form.useWatch('opportunity_id', form);
  const watchedCustomerId = Form.useWatch('customer_id', form);
  const watchedCustomerName = Form.useWatch('customer_name', form);
  const { data: selectedOpportunity } = useOpportunity(watchedOppId || undefined);
  const { data: opportunityOrcs = [] } = useQuery({
    queryKey: ['order-review-checklist', 'opportunity', watchedOppId],
    queryFn: () => orderReviewChecklistService.listByOpportunity(watchedOppId!),
    enabled: Boolean(watchedOppId) && !isEditMode,
  });
  const latestOrcNumber = String(opportunityOrcs[0]?.orc_number || '').trim();
  const [lineItems, setLineItems] = useState<QuotationItemFormData[]>([]);
  const [subtotal, setSubtotal] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [totalAfterDiscount, setTotalAfterDiscount] = useState(0);
  const [vatApplicable, setVatApplicable] = useState(true);
  const [vatPercentage, setVatPercentage] = useState(5);
  const [vatAmount, setVatAmount] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);

  const quotation = quotationData;
  const isTradingRental = !!quotation?.trading_rental_estimation_id;
  const showRentalLineColumns = isTradingRental || hasAnyRentalQuotationLine(lineItems);

  const lineShowsRentalEditors = (record: QuotationItemFormData) =>
    showRentalFieldEditors(record, { quotationFromTradingRental: isTradingRental });
  const customers =
    (Array.isArray(customersData)
      ? customersData
      : (customersData as { data?: unknown[] } | undefined)?.data) ?? [];

  // Helper function to safely convert to number
  const toNumber = (value: any): number => {
    if (value === null || value === undefined) return 0;
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  // Load quotation data in edit mode
  useEffect(() => {
    console.log('📝 [QuotationFormPage] Edit mode:', isEditMode, 'Quotation:', quotation);
    if (isEditMode && quotation) {
      form.setFieldsValue({
        quotation_name: quotation.quotation_name,
        inquiry_reference: quotation.inquiry_reference,
        sap_number: quotation.sap_number,
        customer_id: quotation.customer_id,
        customer_name: quotation.customer_name,
        customer_address: quotation.customer_address,
        customer_contact: quotation.customer_contact,
        project_id: quotation.project_id || undefined,
        project_name: quotation.project_name,
        department_id: quotation.department_id || undefined,
        quotation_date: quotation.quotation_date ? dayjs(quotation.quotation_date) : dayjs(),
        valid_until: quotation.valid_until ? dayjs(quotation.valid_until) : null,
        validity_days: toNumber(quotation.validity_days),
        currency: quotation.currency,
        payment_terms: quotation.payment_terms,
        delivery_terms: quotation.delivery_terms,
        advance_payment_percentage: toNumber(quotation.advance_payment_percentage),
        vat_applicable: quotation.vat_applicable,
        vat_percentage: toNumber(quotation.vat_percentage),
        discount_percentage: toNumber(quotation.discount_percentage),
        notes: quotation.notes,
        terms_and_conditions: quotation.terms_and_conditions,
        special_instructions: quotation.special_instructions,
        opportunity_id: quotation.opportunity_id,
        revenue_service_type_id: quotation.revenue_service_type_id || undefined,
      });

      setVatApplicable(quotation.vat_applicable);
      setVatPercentage(toNumber(quotation.vat_percentage));
      setDiscountPercentage(toNumber(quotation.discount_percentage));

      if (quotation.items && quotation.items.length > 0) {
        // Sort items by line_number to maintain BOQ order
        const sortedItems = [...quotation.items].sort((a, b) => a.line_number - b.line_number);
        
        const items = sortedItems.map((item, index) => ({
          key: `item-${index}`,
          id: item.id,
          item_code: item.item_code,
          item_description: item.item_description,
          revision: item.revision ?? undefined,
          item_category: item.item_category,
          quantity: toNumber(item.quantity),
          uom: item.uom,
          unit_price: toNumber(item.unit_price),
          discount_percentage: toNumber(item.discount_percentage),
          vat_applicable: item.vat_applicable,
          vat_percentage: toNumber(item.vat_percentage),
          notes: item.notes,
          specifications: item.specifications,
          delivery_days: item.delivery_days,
          trading_rental_estimation_line_id: item.trading_rental_estimation_line_id,
          line_type:
            item.line_type ||
            (item.trading_rental_estimation_line_id &&
            (item.rental_period_type ||
              item.rental_start_date ||
              item.rental_end_date ||
              toNumber(item.rental_duration) > 0 ||
              toNumber(item.rental_rate) > 0)
              ? 'Rental'
              : item.trading_rental_estimation_line_id && quotation.trading_rental_estimation_id
                ? 'Rental'
                : undefined),
          rental_period_type: item.rental_period_type,
          rental_start_date: item.rental_start_date,
          rental_end_date: item.rental_end_date,
          rental_duration: toNumber(item.rental_duration),
          rental_rate: toNumber(item.rental_rate) || toNumber(item.unit_price),
          security_deposit: toNumber(item.security_deposit),
          mobilization_charges: toNumber(item.mobilization_charges),
          demobilization_charges: toNumber(item.demobilization_charges),
          operator_charges: toNumber(item.operator_charges),
          transport_charges: toNumber(item.transport_charges),
          damage_charges: toNumber(item.damage_charges),
          net_amount: toNumber(item.net_amount),
          line_total: toNumber(item.line_total),
        }));
        setLineItems(items);
      }
    }
  }, [isEditMode, quotation, form]);

  const quotationEditCheck = useMemo(
    () => (quotation ? canEditQuotation(quotation) : { ok: true }),
    [quotation]
  );

  useEffect(() => {
    if (isEditMode) return;
    message.info(
      'Sales quotations must be converted from a BOQ, multi-item, or trading/rental estimation.'
    );
    navigate('/growth/estimations', { replace: true });
  }, [isEditMode, navigate]);

  useEffect(() => {
    if (!isEditMode || quotationLoading || !quotation || !id) return;
    const editCheck = canEditQuotation(quotation);
    if (!editCheck.ok) {
      message.warning(editCheck.reason || 'This quotation cannot be edited.');
      navigate(`/growth/quotations/${id}`, { replace: true });
    }
  }, [isEditMode, quotationLoading, quotation, id, navigate]);

  useEffect(() => {
    if (isEditMode || !opportunityIdFromUrl) return;
    form.setFieldsValue({ opportunity_id: opportunityIdFromUrl });
  }, [isEditMode, opportunityIdFromUrl, form]);

  useEffect(() => {
    if (!watchedOppId || !selectedOpportunity?.revenue_service_type_id) return;
    form.setFieldValue('revenue_service_type_id', selectedOpportunity.revenue_service_type_id);
  }, [watchedOppId, selectedOpportunity?.revenue_service_type_id, form]);

  useEffect(() => {
    if (isEditMode || !watchedOppId) return;
    const contact = String(selectedOpportunity?.contact_person || '').trim();
    if (contact) {
      form.setFieldValue('customer_contact', contact);
    }
    if (latestOrcNumber) {
      form.setFieldValue('inquiry_reference', latestOrcNumber);
    }
  }, [
    isEditMode,
    watchedOppId,
    selectedOpportunity?.contact_person,
    latestOrcNumber,
    form,
  ]);

  // Calculate totals whenever line items or discount/vat change
  useEffect(() => {
    const useRentalLineMath =
      isTradingRental || lineItems.some((item) => isRentalQuotationItem(item));
    let calcSubtotal = 0;
    let calcLineVat = 0;
    if (useRentalLineMath) {
      for (const item of lineItems) {
        const t = calculateQuotationLineTotals({
          ...item,
          vat_applicable: item.vat_applicable ?? vatApplicable,
        });
        calcSubtotal += t.net_amount;
        calcLineVat += t.vat_amount;
      }
    } else {
      calcSubtotal = lineItems.reduce((sum, item) => {
        const qty = Number(item.quantity) || 0;
        const price = Number(item.unit_price) || 0;
        return sum + qty * price;
      }, 0);
    }

    setSubtotal(calcSubtotal);

    const calcDiscountAmount = (calcSubtotal * discountPercentage) / 100;
    setDiscountAmount(calcDiscountAmount);

    const calcTotalAfterDiscount = calcSubtotal - calcDiscountAmount;
    setTotalAfterDiscount(calcTotalAfterDiscount);

    const calcVatAmount =
      useRentalLineMath && calcLineVat > 0.005
        ? calcLineVat
        : vatApplicable
          ? (calcTotalAfterDiscount * vatPercentage) / 100
          : 0;
    setVatAmount(calcVatAmount);

    const calcGrandTotal = calcTotalAfterDiscount + calcVatAmount;
    setGrandTotal(calcGrandTotal);
  }, [lineItems, discountPercentage, vatApplicable, vatPercentage, isTradingRental]);

  const handleBack = () => {
    navigate('/growth/quotations');
  };

  const handleAddLineItem = () => {
    const newItem: QuotationItemFormData = {
      key: `item-${Date.now()}`,
      item_code: '',
      item_description: '',
      revision: '',
      item_category: '',
      quantity: 1,
      uom: 'PCS',
      unit_price: 0,
      discount_percentage: 0,
      vat_applicable: true,
      vat_percentage: 5
    };
    setLineItems([...lineItems, newItem]);
  };

  const handleDeleteLineItem = (key: string) => {
    setLineItems(lineItems.filter(item => item.key !== key));
  };

  const applyRentalLineFieldUpdate = (
    item: QuotationItemFormData,
    field: string,
    value: unknown
  ): QuotationItemFormData => {
    const next = { ...item, [field]: value } as QuotationItemFormData;
    if (field === 'unit_price' && isRentalQuotationItem(next)) {
      next.rental_rate = Number(value) || 0;
    }
    if (field === 'rental_rate') {
      next.unit_price = Number(value) || 0;
    }
    if (
      showRentalFieldEditors(next, { quotationFromTradingRental: isTradingRental }) &&
      (field === 'rental_period_type' ||
        field === 'rental_start_date' ||
        field === 'rental_end_date')
    ) {
      next.rental_duration = deriveRentalDuration(next);
    }
    if (showRentalFieldEditors(next, { quotationFromTradingRental: isTradingRental })) {
      if (!next.line_type || String(next.line_type).toLowerCase() === 'rental') {
        next.line_type = 'Rental';
      }
      next.specifications = buildRentalSpecificationsSummary(next);
    }
    return next;
  };

  const handleLineItemChange = (key: string, field: string, value: unknown) => {
    setLineItems(
      lineItems.map((item) => {
        if (item.key !== key) return item;
        return applyRentalLineFieldUpdate(item, field, value);
      })
    );
  };

  const handleCustomerChange = async (customerId: string) => {
    if (!customerId) return;
    const customer = customers.find((c: { id: string }) => c.id === customerId);
    if (customer) {
      form.setFieldValue('customer_name', (customer as { customer_name?: string }).customer_name);
    }
    try {
      const response = await customerService.getCustomerById(customerId);
      const detail = (response as unknown as { data?: Record<string, unknown> })?.data ?? response;
      const detailRecord = detail as Record<string, unknown>;
      const address = formatCustomerBillingAddress(detailRecord);
      if (address) {
        form.setFieldValue('customer_address', address);
      }
      const contactFromOpportunity = String(selectedOpportunity?.contact_person ?? '').trim();
      const contact = contactFromOpportunity || String(detailRecord.contact_person ?? '').trim();
      if (contact) {
        form.setFieldValue('customer_contact', contact);
      }
    } catch {
      // Keep manual entry when customer detail is unavailable
    }
  };

  const handleSubmit = async (values: any) => {
    if (lineItems.length === 0) {
      message.error('Please add at least one line item');
      return;
    }

    const quotationData: QuotationRequest = {
      quotation_name: values.quotation_name,
      inquiry_reference: values.inquiry_reference?.trim() || undefined,
      sap_number: values.sap_number?.trim() || undefined,
      customer_id: values.customer_id,
      customer_name: values.customer_name,
      customer_address: values.customer_address?.trim() || undefined,
      customer_contact: values.customer_contact,
      project_id: values.project_id,
      project_name: values.project_name,
      department_id: values.department_id,
      quotation_date: values.quotation_date ? values.quotation_date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
      valid_until: values.valid_until ? values.valid_until.format('YYYY-MM-DD') : undefined,
      validity_days: values.validity_days,
      currency: values.currency || baseCurrency?.code || 'AED',
      payment_terms: values.payment_terms,
      delivery_terms: values.delivery_terms,
      advance_payment_percentage: values.advance_payment_percentage || 0,
      vat_applicable: values.vat_applicable,
      vat_percentage: values.vat_percentage,
      discount_percentage: values.discount_percentage || 0,
      notes: values.notes,
      terms_and_conditions: values.terms_and_conditions,
      special_instructions: values.special_instructions,
      opportunity_id: values.opportunity_id || undefined,
      revenue_service_type_id: values.revenue_service_type_id || undefined,
      items: lineItems.map((item) => {
        return {
          id: isEditMode ? item.id : undefined,
          item_code: item.item_code,
          item_description: item.item_description,
          revision: (item as { revision?: string }).revision?.trim() || null,
          item_category: item.item_category,
          quantity: item.quantity,
          uom: item.uom,
          unit_price: isRentalQuotationItem(item)
            ? item.rental_rate ?? item.unit_price
            : item.unit_price,
          discount_percentage: item.discount_percentage || 0,
          vat_applicable: item.vat_applicable,
          vat_percentage: item.vat_percentage,
          notes: item.notes,
          specifications: item.specifications,
          delivery_days: item.delivery_days,
          trading_rental_estimation_line_id: item.trading_rental_estimation_line_id,
          line_type: item.line_type,
          rental_period_type: item.rental_period_type,
          rental_start_date: item.rental_start_date,
          rental_end_date: item.rental_end_date,
          rental_duration: item.rental_duration,
          rental_rate: item.rental_rate ?? item.unit_price,
          security_deposit: item.security_deposit,
          mobilization_charges: item.mobilization_charges,
          demobilization_charges: item.demobilization_charges,
          operator_charges: item.operator_charges,
          transport_charges: item.transport_charges,
          damage_charges: item.damage_charges,
        };
      })
    };

    try {
      if (isEditMode) {
        await updateMutation.mutateAsync({ id: id!, data: quotationData });
        message.success('Quotation updated successfully');
        navigate(`/growth/quotations/${id}`);
      } else {
        const response = await createMutation.mutateAsync(quotationData);
        message.success('Quotation created successfully');
        const newQuotationId = response?.data?.id;
        if (newQuotationId) {
          navigate(`/growth/quotations/${newQuotationId}`);
        } else {
          navigate('/growth/quotations');
        }
      }
    } catch (error: any) {
      console.error('Error saving quotation:', error);
      message.error(error.response?.data?.message || 'Failed to save quotation');
    }
  };

  if (quotationLoading && isEditMode) {
    return (
      <div style={{ padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" tip="Loading quotation..." />
      </div>
    );
  }

  if (isEditMode && quotation && !quotationEditCheck.ok) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          type="warning"
          showIcon
          message="Quotation cannot be edited"
          description={quotationEditCheck.reason}
          action={
            <Button onClick={() => navigate(`/growth/quotations/${id}`)}>
              Back to quotation
            </Button>
          }
        />
      </div>
    );
  }

  // Line items table columns
  const itemColumns: ColumnsType<QuotationItemFormData> = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_, __, index) => index + 1
    },
    {
      title: 'Item Code',
      dataIndex: 'item_code',
      key: 'item_code',
      width: 120,
      render: (value, record) => (
        <Input
          value={value}
          onChange={(e) => handleLineItemChange(record.key, 'item_code', e.target.value)}
          placeholder="Item Code"
          size="small"
        />
      )
    },
    {
      title: 'Description *',
      dataIndex: 'item_description',
      key: 'item_description',
      width: 200,
      render: (value, record) => (
        <Input.TextArea
          value={value}
          onChange={(e) => handleLineItemChange(record.key, 'item_description', e.target.value)}
          placeholder="Description"
          size="small"
          rows={2}
        />
      )
    },
    {
      title: 'Revision',
      dataIndex: 'revision',
      key: 'revision',
      width: 90,
      render: (value, record) => (
        <Input
          value={value}
          onChange={(e) => handleLineItemChange(record.key, 'revision', e.target.value)}
          placeholder="Rev"
          size="small"
          maxLength={50}
        />
      ),
    },
    {
      title: 'Quantity *',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (value, record) => (
        <InputNumber
          value={value}
          onChange={(val) => handleLineItemChange(record.key, 'quantity', val)}
          min={0}
          step={0.01}
          precision={2}
          size="small"
          style={{ width: '100%' }}
        />
      )
    },
    ...(showRentalLineColumns
      ? [
          {
            title: 'Duration',
            dataIndex: 'rental_duration',
            key: 'rental_duration',
            width: 90,
            render: (value: number, record: QuotationItemFormData) =>
              lineShowsRentalEditors(record) ? (
                <InputNumber
                  value={value}
                  onChange={(val) => handleLineItemChange(record.key, 'rental_duration', val)}
                  min={0}
                  step={1}
                  precision={2}
                  size="small"
                  style={{ width: '100%' }}
                />
              ) : (
                '—'
              ),
          } as ColumnsType<QuotationItemFormData>[number],
          {
            title: 'Period',
            dataIndex: 'rental_period_type',
            key: 'rental_period_type',
            width: 110,
            render: (value: string, record: QuotationItemFormData) =>
              lineShowsRentalEditors(record) ? (
                <Select
                  value={value || 'Daily'}
                  options={[...RENTAL_PERIOD_OPTIONS]}
                  onChange={(val) => handleLineItemChange(record.key, 'rental_period_type', val)}
                  size="small"
                  style={{ width: '100%' }}
                  getPopupContainer={(trigger) => trigger.parentElement || document.body}
                />
              ) : (
                '—'
              ),
          } as ColumnsType<QuotationItemFormData>[number],
          {
            title: 'Start',
            dataIndex: 'rental_start_date',
            key: 'rental_start_date',
            width: 120,
            render: (value: string, record: QuotationItemFormData) =>
              lineShowsRentalEditors(record) ? (
                <DatePicker
                  value={value ? dayjs(value) : null}
                  onChange={(d) =>
                    handleLineItemChange(
                      record.key,
                      'rental_start_date',
                      d ? d.format('YYYY-MM-DD') : undefined
                    )
                  }
                  size="small"
                  style={{ width: '100%' }}
                  format="DD-MM-YYYY"
                  getPopupContainer={(trigger) => trigger.parentElement || document.body}
                />
              ) : (
                '—'
              ),
          } as ColumnsType<QuotationItemFormData>[number],
          {
            title: 'End',
            dataIndex: 'rental_end_date',
            key: 'rental_end_date',
            width: 120,
            render: (value: string, record: QuotationItemFormData) =>
              lineShowsRentalEditors(record) ? (
                <DatePicker
                  value={value ? dayjs(value) : null}
                  onChange={(d) =>
                    handleLineItemChange(
                      record.key,
                      'rental_end_date',
                      d ? d.format('YYYY-MM-DD') : undefined
                    )
                  }
                  size="small"
                  style={{ width: '100%' }}
                  format="DD-MM-YYYY"
                  getPopupContainer={(trigger) => trigger.parentElement || document.body}
                />
              ) : (
                '—'
              ),
          } as ColumnsType<QuotationItemFormData>[number],
          {
            title: 'Extras',
            key: 'rental_extras',
            width: 90,
            render: (_: unknown, record: QuotationItemFormData) =>
              lineShowsRentalEditors(record) ? (
                <Popover
                  title="Rental extra charges"
                  trigger="click"
                  content={
                    <div style={{ width: 220 }}>
                      {(
                        [
                          ['security_deposit', 'Security deposit'],
                          ['mobilization_charges', 'Mobilization'],
                          ['demobilization_charges', 'Demobilization'],
                          ['operator_charges', 'Operator'],
                          ['transport_charges', 'Transport'],
                          ['damage_charges', 'Damage'],
                        ] as const
                      ).map(([field, label]) => (
                        <div key={field} style={{ marginBottom: 8 }}>
                          <Text style={{ fontSize: 12 }}>{label}</Text>
                          <InputNumber
                            value={record[field] as number}
                            onChange={(val) => handleLineItemChange(record.key, field, val)}
                            min={0}
                            step={0.01}
                            precision={2}
                            size="small"
                            style={{ width: '100%' }}
                          />
                        </div>
                      ))}
                    </div>
                  }
                >
                  <Button type="link" size="small">
                    Edit
                  </Button>
                </Popover>
              ) : (
                '—'
              ),
          } as ColumnsType<QuotationItemFormData>[number],
        ]
      : []),
    {
      title: 'UOM',
      dataIndex: 'uom',
      key: 'uom',
      width: 80,
      render: (value, record) => (
        <Input
          value={value}
          onChange={(e) => handleLineItemChange(record.key, 'uom', e.target.value)}
          placeholder="UOM"
          size="small"
        />
      )
    },
    {
      title: showRentalLineColumns ? 'Unit price / Rate *' : 'Unit Price *',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 120,
      render: (value, record) => (
        <InputNumber
          value={isRentalQuotationItem(record) ? record.rental_rate ?? value : value}
          onChange={(val) =>
            handleLineItemChange(
              record.key,
              isRentalQuotationItem(record) ? 'rental_rate' : 'unit_price',
              val
            )
          }
          min={0}
          step={0.01}
          precision={2}
          size="small"
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: 'Net amount',
      key: 'line_total',
      width: 120,
      align: 'right',
      render: (_, record) => {
        const t = calculateQuotationLineTotals({
          ...record,
          vat_applicable: record.vat_applicable ?? vatApplicable,
        });
        return (
          <div>
            <Text>{t.net_amount.toFixed(2)}</Text>
            {isRentalQuotationItem(record) ? (
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {formatRentalQuantity(record)}
                </Text>
              </div>
            ) : null}
          </div>
        );
      }
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteLineItem(record.key)}
          size="small"
        />
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card style={{ marginBottom: 16 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              Back
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              {isEditMode ? 'Edit Quotation' : 'Create New Quotation'}
            </Title>
          </Space>
        </Space>
      </Card>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          quotation_date: dayjs(),
          validity_days: 30,
          currency: baseCurrency?.code || 'AED',
          vat_applicable: true,
          vat_percentage: 5,
          discount_percentage: 0,
          advance_payment_percentage: 0
        }}
      >
        <Row gutter={16}>
          <Col span={16}>
            {/* Basic Information */}
            <Card title="Basic Information" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Subject"
                    name="quotation_name"
                    rules={[{ required: true, message: 'Please enter subject' }]}
                  >
                    <Input placeholder="Enter subject" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Inquiry Reference" name="inquiry_reference">
                    <Input placeholder="Enter inquiry reference" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="SAP#" name="sap_number">
                    <Input placeholder="Enter SAP number" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Customer"
                    name="customer_id"
                  >
                    <Select
                      placeholder="Select customer"
                      onChange={handleCustomerChange}
                      showSearch
                      optionFilterProp="children"
                      allowClear
                    >
                      {customers.map((customer: any) => (
                        <Select.Option key={customer.id} value={customer.id}>
                          {customer.customer_name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Customer Name"
                    name="customer_name"
                    rules={[{ required: true, message: 'Please enter customer name' }]}
                  >
                    <Input placeholder="Enter customer name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Kind Attention"
                    name="customer_contact"
                  >
                    <Input placeholder="Enter contact person / department" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item label="Customer Address" name="customer_address">
                    <Input.TextArea rows={2} placeholder="Enter customer address" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Project"
                    name="project_id"
                    rules={[{ required: true, message: 'Project is required' }]}
                  >
                    <ProjectSelector
                      customerId={watchedCustomerId}
                      customerName={watchedCustomerName}
                      placeholder="Select or create project"
                      onChange={(projectId, projectData) => {
                        form.setFieldsValue({
                          project_id: projectId,
                          project_name: projectData?.project_name,
                        });
                      }}
                    />
                  </Form.Item>
                  <Form.Item name="project_name" hidden>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="department_id"
                    label="Department"
                    rules={[{ required: true, message: 'Department is required' }]}
                  >
                    <DepartmentSelect />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Opportunity (CRM)" name="opportunity_id">
                    <Select
                      placeholder="Link to open opportunity (optional)"
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      options={opportunities.map((o) => ({
                        value: o.id,
                        label: `${o.opportunity_number} — ${o.opportunity_name}`,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Revenue service type"
                    name="revenue_service_type_id"
                    rules={[{ required: true, message: 'Required — used for customer order conversion and invoice GL' }]}
                  >
                    <Select
                      placeholder="Filled from opportunity when linked; required on every quotation"
                      showSearch
                      optionFilterProp="label"
                      options={rstOptions}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    label="Quotation Date"
                    name="quotation_date"
                    rules={[{ required: true, message: 'Please select date' }]}
                  >
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Valid Until"
                    name="valid_until"
                  >
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="Validity Days"
                    name="validity_days"
                  >
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Currency"
                    name="currency"
                  >
                    <Select placeholder="Select currency">
                      <Select.Option value="AED">AED</Select.Option>
                      <Select.Option value="USD">USD</Select.Option>
                      <Select.Option value="EUR">EUR</Select.Option>
                      <Select.Option value="GBP">GBP</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Advance Payment %"
                    name="advance_payment_percentage"
                  >
                    <InputNumber min={0} max={100} step={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Line Items */}
            <Card 
              title="Line Items" 
              extra={
                <Tooltip title="Line items come from the source estimation / BOQ. Add or change lines there.">
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddLineItem} disabled>
                    Add Item
                  </Button>
                </Tooltip>
              }
              style={{ marginBottom: 16 }}
            >
              <Table
                columns={itemColumns}
                dataSource={lineItems}
                pagination={false}
                scroll={{ x: 'max-content' }}
                size="small"
              />
            </Card>

            {/* Additional Information */}
            <Card
              title="Additional Information"
              extra={
                <Tooltip title="Fill empty terms / notes fields from Sales → Quotation defaults">
                  <Button size="small" onClick={applyCompanyDefaults} disabled={!quotationDefaults}>
                    Apply company defaults
                  </Button>
                </Tooltip>
              }
            >
              <Form.Item label="Payment Terms" name="payment_terms">
                <PaymentTermSelect />
              </Form.Item>

              <Form.Item label="Delivery Terms" name="delivery_terms">
                <FormattedTextArea minHeight={72} placeholder="Enter delivery terms (paste keeps formatting)" />
              </Form.Item>

              <Form.Item label="Notes" name="notes">
                <FormattedTextArea minHeight={96} placeholder="Enter notes (paste keeps formatting)" />
              </Form.Item>

              <Form.Item label="Terms and Conditions" name="terms_and_conditions">
                <FormattedTextArea
                  minHeight={120}
                  placeholder="Enter terms and conditions (paste keeps formatting)"
                />
              </Form.Item>

              <Form.Item label="Special Instructions" name="special_instructions">
                <FormattedTextArea
                  minHeight={72}
                  placeholder="Enter special instructions (paste keeps formatting)"
                />
              </Form.Item>
            </Card>
          </Col>

          <Col span={8}>
            {/* Financial Summary */}
            <Card title="Financial Summary" style={{ marginBottom: 16, position: 'sticky', top: 24 }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                  <Text type="secondary">Subtotal</Text>
                  <div>
                    <Text strong style={{ fontSize: 16 }}>
                      {form.getFieldValue('currency') || 'AED'} {subtotal.toFixed(2)}
                    </Text>
                  </div>
                </div>

                <Divider style={{ margin: '8px 0' }} />

                <div>
                  <Form.Item
                    label="Discount %"
                    name="discount_percentage"
                    style={{ marginBottom: 8 }}
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      step={0.1}
                      style={{ width: '100%' }}
                      onChange={(val) => setDiscountPercentage(val || 0)}
                    />
                  </Form.Item>
                  <Text type="secondary">Discount Amount: {form.getFieldValue('currency') || 'AED'} {discountAmount.toFixed(2)}</Text>
                </div>

                <div>
                  <Text type="secondary">After Discount</Text>
                  <div>
                    <Text strong>
                      {form.getFieldValue('currency') || 'AED'} {totalAfterDiscount.toFixed(2)}
                    </Text>
                  </div>
                </div>

                <Divider style={{ margin: '8px 0' }} />

                <div>
                  <Form.Item
                    label="VAT Applicable"
                    name="vat_applicable"
                    valuePropName="checked"
                    style={{ marginBottom: 8 }}
                  >
                    <Switch onChange={setVatApplicable} />
                  </Form.Item>
                  
                  {vatApplicable && (
                    <Form.Item
                      label="VAT %"
                      name="vat_percentage"
                      style={{ marginBottom: 8 }}
                    >
                      <InputNumber
                        min={0}
                        max={100}
                        step={0.1}
                        style={{ width: '100%' }}
                        onChange={(val) => setVatPercentage(val || 0)}
                      />
                    </Form.Item>
                  )}
                  <Text type="secondary">VAT Amount: {form.getFieldValue('currency') || 'AED'} {vatAmount.toFixed(2)}</Text>
                </div>

                <Divider style={{ margin: '8px 0' }} />

                <div style={{ backgroundColor: '#f0f2f5', padding: '12px', borderRadius: '4px' }}>
                  <Text type="secondary">Grand Total</Text>
                  <div>
                    <Text strong style={{ fontSize: 20, color: '#cf1322' }}>
                      {form.getFieldValue('currency') || 'AED'} {grandTotal.toFixed(2)}
                    </Text>
                  </div>
                </div>

                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  size="large"
                  block
                  loading={createMutation.isPending || updateMutation.isPending}
                >
                  {isEditMode ? 'Update Quotation' : 'Create Quotation'}
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default QuotationFormPage;
